// Mock data for BarknFurs Grooming Studio website
// (Frontend mock — backend will replace booking & contact submissions)

import {
  Bath, Scissors, Sparkles, Wind, ShieldCheck, Heart, Home as HomeIcon, Stethoscope,
} from "lucide-react";

export const BRAND = {
  name: "BarknFurs Grooming Studio",
  shortName: "BarknFurs",
  tagline: "Premium Pet Grooming in Bhubaneswar",
  phone: "+91 88833 39158",
  phoneRaw: "918883339158",
  whatsapp: "918883339158",
  email: "hello@barknfurs.in",
  address: "Vim-142, Vim Colony, Fire Station Rd, Opp. Shailashree Vihar, Chandrasekharpur, Bhubaneswar, Odisha 751021",
  city: "Bhubaneswar",
  state: "Odisha",
  hours: "Mon – Sun · 9:00 AM – 9:00 PM",
  logoUrl: "/brand/logo-color.png",
  logoWhiteUrl: "/brand/BarknFurs_Logo_White_on_Purple.png",
  social: {
    instagram: "https://instagram.com/",
    facebook: "https://facebook.com/",
  },
};

export const SERVICES = [
  {
    id: "bathing",
    icon: Bath,
    title: "Luxury Bathing & Shampoo",
    short: "Deep cleansing wash with premium shampoos & conditioners.",
    description:
      "A relaxing warm-water bath using vet-approved, pH-balanced shampoos. Removes dirt, odour and dander while keeping your pet's skin soft.",
    duration: "45–60 min",
    fromPrice: 600,
    image:
      "https://images.unsplash.com/photo-1581887936036-3f4f7f0b6679?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    highlights: ["Hypoallergenic shampoo", "Warm water rinse", "Blow dry & brush"],
  },
  {
    id: "haircut",
    icon: Scissors,
    title: "Professional Haircut & Styling",
    short: "Breed-specific cuts and stylish trims by expert groomers.",
    description:
      "Tailored haircuts from puppy cuts to show-style trims. Our groomers shape, scissor and style based on your pet's breed, coat and your preference.",
    duration: "60–90 min",
    fromPrice: 999,
    image:
      "https://images.pexels.com/photos/6131576/pexels-photo-6131576.jpeg?auto=compress&cs=tinysrgb&w=900",
    highlights: ["Breed-specific styling", "Sanitary trim", "Paw pad shaving"],
  },
  {
    id: "nail-trim",
    icon: Sparkles,
    title: "Nail Trimming & Filing",
    short: "Gentle nail clipping and smoothing for happy paws.",
    description:
      "Overgrown nails cause discomfort and posture issues. We carefully trim and file nails using safe clippers — quick, calm and pain-free.",
    duration: "15–20 min",
    fromPrice: 199,
    image:
      "https://images.unsplash.com/photo-1583511655826-05700d52f4d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    highlights: ["Safe clippers", "Smoothing file", "Stress-free handling"],
  },
  {
    id: "ear-cleaning",
    icon: Stethoscope,
    title: "Ear & Eye Cleaning",
    short: "Removes wax, debris and tear-stains gently.",
    description:
      "Routine ear and eye cleaning prevents infections, ear-mites and tear staining. We use vet-approved solutions with cotton wipes — never inserts.",
    duration: "10–15 min",
    fromPrice: 149,
    image:
      "https://images.unsplash.com/photo-1612774412771-005ed8e861d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    highlights: ["Vet-approved solution", "Soft cotton wipes", "Tear-stain removal"],
  },
  {
    id: "deshedding",
    icon: Wind,
    title: "De-shedding Treatment",
    short: "Reduces shedding by up to 90% — say goodbye to fur on sofas.",
    description:
      "Deep undercoat treatment using FURminator-style tools and a high-velocity dryer. Ideal for double-coated breeds, huskies, GSDs, retrievers and Persians.",
    duration: "60 min",
    fromPrice: 999,
    image:
      "https://images.pexels.com/photos/4587959/pexels-photo-4587959.jpeg?auto=compress&cs=tinysrgb&w=900",
    highlights: ["Undercoat removal", "High-velocity blow dry", "Less hair on furniture"],
  },
  {
    id: "dry-bath",
    icon: ShieldCheck,
    title: "Dry Bath & Refresh",
    short: "Quick water-free refresh between full baths.",
    description:
      "Gentle dry shampoo wipe-down, brush-out and cologne spritz. Perfect for in-between full baths or anxious pets that prefer to skip the water — your pet smells fresh in 20 minutes.",
    duration: "20–30 min",
    fromPrice: 499,
    image:
      "https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    highlights: ["Water-free wash", "Quick brush-out", "Cologne spritz"],
  },
  {
    id: "skin-care",
    icon: Heart,
    title: "Plush Puppy Protein Pack",
    short: "Coat rescue treatment for dry, damaged or brittle fur.",
    description:
      "Revive dry, damaged or brittle coats with Plush Puppy Coat Rescue + Sea Breeze Oil. Restores strength, moisture and elasticity. Ideal for show dogs and pets needing extra coat nourishment.",
    duration: "60–75 min",
    fromPrice: 1499,
    image:
      "https://images.pexels.com/photos/5731866/pexels-photo-5731866.jpeg?auto=compress&cs=tinysrgb&w=900",
    highlights: ["Oatmeal soak", "Aloe vera mask", "Coat moisturiser"],
  },
  {
    id: "home-grooming",
    icon: HomeIcon,
    title: "Home Pet Grooming",
    short: "Same studio prices, delivered at your doorstep.",
    description:
      "Stress-free for anxious pets. Our groomer arrives with portable bathing equipment, dryers and tools. Available across Bhubaneswar — and we don't charge anything extra for home service.",
    duration: "60–120 min",
    fromPrice: 600,
    image:
      "https://images.pexels.com/photos/7516843/pexels-photo-7516843.jpeg?auto=compress&cs=tinysrgb&w=900",
    highlights: ["Doorstep service", "All equipment carried", "Anxious-pet friendly"],
  },
];

// Breed-specific pricing — extracted directly from BarknFurs official price list
// Columns: bath, hygiene (Bath + Hygiene Care), haircut (Bath + Hair Cut), zeroTrim (Bath + Zero Trim)
// null = service not offered for that breed/age
// All prices in INR.
const BREEDS_RAW = [
  { name: "English Mastiff", category: "Large", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: null },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1500, zeroTrim: null },
    { age: "5–6 mo", bath: 1400, hygiene: 1600, haircut: 1900, zeroTrim: null },
    { age: "7+ mo", bath: 1800, hygiene: 2000, haircut: null, zeroTrim: 2400 },
  ]},
  { name: "Chow Chow", category: "Long Coat", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1200, zeroTrim: 1400 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1800, zeroTrim: 1600 },
    { age: "5–6 mo", bath: 1400, hygiene: 1700, haircut: 2200, zeroTrim: 2000 },
    { age: "7+ mo", bath: 1800, hygiene: 2200, haircut: 2800, zeroTrim: 2500 },
  ]},
  { name: "Golden Retriever", category: "Long Coat", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1200, zeroTrim: 1400 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1800, zeroTrim: 1600 },
    { age: "5–6 mo", bath: 1400, hygiene: 1700, haircut: 2200, zeroTrim: 2000 },
    { age: "7+ mo", bath: 1800, hygiene: 2200, haircut: 2800, zeroTrim: 2500 },
  ]},
  { name: "Labrador Retriever", category: "Medium", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1400 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1600 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: null, zeroTrim: 1800 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: null, zeroTrim: 2000 },
  ]},
  { name: "Pomeranian", category: "Small", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "Maltese", category: "Small", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "Dalmatian", category: "Medium", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1400 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1600 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: null, zeroTrim: 1800 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: null, zeroTrim: 2000 },
  ]},
  { name: "Boxer", category: "Medium", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1400 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1600 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: null, zeroTrim: 1800 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: null, zeroTrim: 2000 },
  ]},
  { name: "Shih Tzu", category: "Small", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "Lhasa Apso", category: "Small", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "German Shepherd", category: "Large", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1600, zeroTrim: 1400 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1800, zeroTrim: 1600 },
    { age: "5–6 mo", bath: 1400, hygiene: 1700, haircut: 2200, zeroTrim: 2000 },
    { age: "7+ mo", bath: 1800, hygiene: 2000, haircut: 2800, zeroTrim: 2500 },
  ]},
  { name: "American Bully", category: "Medium", rows: [
    { age: "0–2 mo", bath: 600, hygiene: 800, haircut: null, zeroTrim: null },
    { age: "3–4 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1300 },
    { age: "5–6 mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1400 },
    { age: "7+ mo", bath: 1200, hygiene: 1400, haircut: null, zeroTrim: 1600 },
  ]},
  { name: "Pug", category: "Small", rows: [
    { age: "0–2 mo", bath: 600, hygiene: 800, haircut: null, zeroTrim: 1100 },
    { age: "3–4 mo", bath: 700, hygiene: 900, haircut: null, zeroTrim: 1300 },
    { age: "5–6 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1400 },
    { age: "7+ mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1600 },
  ]},
  { name: "Beagle", category: "Small", rows: [
    { age: "0–2 mo", bath: 600, hygiene: 800, haircut: null, zeroTrim: 1100 },
    { age: "3–4 mo", bath: 700, hygiene: 900, haircut: null, zeroTrim: 1300 },
    { age: "5–6 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1400 },
    { age: "7+ mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1600 },
  ]},
  { name: "Husky", category: "Long Coat", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1600, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1800, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1600, hygiene: 1800, haircut: 2200, zeroTrim: 1800 },
    { age: "7+ mo", bath: 2200, hygiene: 2400, haircut: 2800, zeroTrim: 2200 },
  ]},
  { name: "Poodle", category: "Small", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "Rottweiler", category: "Large", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1300 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1500 },
    { age: "5–6 mo", bath: 1400, hygiene: 1600, haircut: null, zeroTrim: 1900 },
    { age: "7+ mo", bath: 1800, hygiene: 2000, haircut: null, zeroTrim: 2400 },
  ]},
  { name: "Dachshund", category: "Small", rows: [
    { age: "0–2 mo", bath: 600, hygiene: 800, haircut: null, zeroTrim: 1100 },
    { age: "3–4 mo", bath: 700, hygiene: 900, haircut: null, zeroTrim: 1300 },
    { age: "5–6 mo", bath: 800, hygiene: 1000, haircut: null, zeroTrim: 1400 },
    { age: "7+ mo", bath: 1000, hygiene: 1200, haircut: null, zeroTrim: 1600 },
  ]},
  { name: "Saint Bernard", category: "Large", rows: [
    { age: "0–2 mo", bath: 1000, hygiene: 1800, haircut: 2000, zeroTrim: 1600 },
    { age: "3–4 mo", bath: 1800, hygiene: 2000, haircut: 2400, zeroTrim: 1800 },
    { age: "5–6 mo", bath: 2200, hygiene: 2400, haircut: 3000, zeroTrim: 2200 },
    { age: "7+ mo", bath: 2800, hygiene: 3000, haircut: 3800, zeroTrim: 2800 },
  ]},
  { name: "Indian Pariah Dog", category: "Medium", rows: [
    { age: "0–2 mo", bath: 600, hygiene: 1000, haircut: null, zeroTrim: 1100 },
    { age: "3–4 mo", bath: 700, hygiene: 1200, haircut: null, zeroTrim: 1300 },
    { age: "5–6 mo", bath: 800, hygiene: 1400, haircut: null, zeroTrim: 1400 },
    { age: "7+ mo", bath: 1000, hygiene: 1600, haircut: null, zeroTrim: 1600 },
  ]},
  { name: "English Cocker Spaniel", category: "Medium", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "Yorkshire Terrier", category: "Small", rows: [
    { age: "0–2 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "3–4 mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
    { age: "5–6 mo", bath: 1200, hygiene: 1400, haircut: 2000, zeroTrim: 1600 },
    { age: "7+ mo", bath: 1400, hygiene: 1600, haircut: 2200, zeroTrim: 1800 },
  ]},
  { name: "Cat", category: "Cat", rows: [
    { age: "0–2 mo", bath: 600, hygiene: 800, haircut: 1200, zeroTrim: 1000 },
    { age: "3–4 mo", bath: 800, hygiene: 1000, haircut: 1400, zeroTrim: 1200 },
    { age: "5–6 mo", bath: 900, hygiene: 1100, haircut: 1500, zeroTrim: 1400 },
    { age: "7+ mo", bath: 1000, hygiene: 1200, haircut: 1600, zeroTrim: 1400 },
  ]},
];

export const BREEDS = BREEDS_RAW.sort((a, b) => a.name.localeCompare(b.name));

export const PRICING_COLUMNS = [
  { key: "bath", label: "Bath" },
  { key: "hygiene", label: "Bath + Hygiene Care" },
  { key: "haircut", label: "Bath + Hygiene Care + Hair Cut (Full Body)" },
  { key: "zeroTrim", label: "Bath + Zero Trim" },
];

export const ADD_ONS = [
  { name: "Ear Clean", price: 149 },
  { name: "Nail Cutting", price: 199 },
  { name: "Eye Care", price: 149 },
  { name: "Teeth Brush", price: 149 },
  { name: "Paw Care", price: 149 },
  { name: "Hygiene Cut", price: 199 },
  { name: "Cologne", price: 149 },
  { name: "Dry Bath", price: 499 },
  { name: "Hygiene Care", price: 499 },
  { name: "Deshedding", price: 999 },
  { name: "Dematting", price: 999 },
  { name: "Haircut / Zero Trim", price: 999 },
  { name: "Protein Pack", price: 1499 },
];

export const SPECIAL_TREATMENT = {
  name: "Plush Puppy Protein Pack",
  price: 1499,
  description:
    "Revive dry, damaged or brittle coats with this powerful blend of Plush Puppy Coat Rescue and Sea Breeze Oil. Restores strength, moisture and elasticity to dehydrated coats. Ideal for show dogs or pets needing extra coat nourishment — leaves fur soft, shiny and resilient.",
};

export const OFFERS = [
  { id: "oil-massage", title: "Warm Oil Massage", tag: "FREE", detail: "On every grooming session — deep skin & coat nourishment." },
  { id: "toy-pack-549", title: "Treat & Toy Pack", tag: "Worth ₹549 · FREE", detail: "With every Full Body Grooming." },
  { id: "toy-pack-349", title: "Treat & Toy Pack", tag: "Worth ₹349 · FREE", detail: "With every Bath & Hygiene Care session." },
  { id: "dematting", title: "Dematting & Deshedding", tag: "FREE", detail: "Professional dematting & deshedding on every grooming service." },
];
export const OFFER_NOTE = "Offer valid per session; customers may choose 1 offer per booking. Codes are valid for 30 days.";

export const PRICING_NOTES = [
  "No extra charge for home grooming — same studio prices delivered at your doorstep across Bhubaneswar.",
  "Current offers: FREE Warm Oil Massage, FREE Treat & Toy Pack (₹549 with Full Body Grooming / ₹349 with Bath & Hygiene Care) or FREE Dematting & Deshedding — 1 offer per booking.",
  "All prices are subject to additional charges depending on condition of coat.",
  "Deshedding and heavy matted coats are charged extra based on breed, size and condition — unless you pick the FREE Dematting & Deshedding offer.",
  "Don't see your breed listed? Call us at +91 88833 39158 — we groom every breed.",
];

export const TESTIMONIALS = [
  {
    name: "Ananya Mishra",
    pet: "Coco · Golden Retriever",
    rating: 5,
    text:
      "Coco is usually anxious at groomers, but the team at BarknFurs were so patient and kind. He came home looking like a star and smelled amazing!",
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
  {
    name: "Rohit Sahoo",
    pet: "Bruno · German Shepherd",
    rating: 5,
    text:
      "Best de-shedding service in Bhubaneswar — hands down. Bruno used to leave fur everywhere, now my sofa is finally clean. Worth every rupee.",
    avatar:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
  {
    name: "Priya Patnaik",
    pet: "Mochi · Persian Cat",
    rating: 5,
    text:
      "Booked the home grooming service for Mochi and it was the best decision. The groomer was professional, gentle and Mochi didn't stress at all.",
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
  {
    name: "Aditya Behera",
    pet: "Max · Labrador",
    rating: 5,
    text:
      "Affordable, clean studio with friendly staff. Max actually wags his tail when we pull up here now. Highly recommended in Bhubaneswar!",
    avatar:
      "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?crop=entropy&cs=srgb&fm=jpg&q=85&w=200",
  },
];

export const GALLERY = [
  {
    title: "Spa Day Glow",
    pet: "Pomeranian",
    src: "https://images.unsplash.com/photo-1611173622933-91942d394b04?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    title: "Show-Style Cut",
    pet: "Schnauzer",
    src: "https://images.pexels.com/photos/6131576/pexels-photo-6131576.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    title: "Bath Time Bliss",
    pet: "Beagle",
    src: "https://images.unsplash.com/photo-1581887936036-3f4f7f0b6679?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    title: "Cat Couture",
    pet: "Persian",
    src: "https://images.pexels.com/photos/29287960/pexels-photo-29287960.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    title: "De-shedding Hero",
    pet: "German Shepherd",
    src: "https://images.pexels.com/photos/4587959/pexels-photo-4587959.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    title: "Doorstep Pamper",
    pet: "Golden Retriever",
    src: "https://images.pexels.com/photos/7516843/pexels-photo-7516843.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    title: "Velvet Coat Finish",
    pet: "Shih Tzu",
    src: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    title: "Happy Tail Wags",
    pet: "Labrador",
    src: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

export const TEAM = [
  {
    name: "Suman Kar",
    role: "Lead Groomer & Founder",
    bio: "8+ years of experience. Certified pet stylist with expertise in show cuts and breed-specific styling.",
    img: "https://images.unsplash.com/photo-1607746882042-944635dfe10e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    name: "Niharika Das",
    role: "Senior Pet Stylist",
    bio: "Specialist in cat grooming and de-shedding therapy. Loves Persians and ragdolls.",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
  {
    name: "Ravi Mohanty",
    role: "Home Grooming Lead",
    bio: "Bhubaneswar's friendliest doorstep groomer. Brings the studio to your living room.",
    img: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  },
];

export const FAQS = [
  {
    q: "How long does a grooming session take?",
    a: "It depends on your pet's coat and the service. A basic bath takes 45 minutes; full grooming or spa deluxe sessions take 1.5–2 hours. We will give you an exact estimate when you book.",
  },
  {
    q: "Do you offer home grooming in Bhubaneswar?",
    a: "Yes! Our Home Pet Grooming service is available across Bhubaneswar including Sailashree Vihar, Patia, Chandrasekharpur, Khandagiri, Saheed Nagar and more. We carry all equipment with us.",
  },
  {
    q: "What products do you use?",
    a: "We use only vet-approved, pet-safe and hypoallergenic products. For sensitive skin, we have specialised oatmeal and aloe-based shampoos. We never use human shampoos on pets.",
  },
  {
    q: "Is grooming safe for senior pets and puppies?",
    a: "Absolutely. Our groomers are trained to handle pets of all ages, including senior dogs and young puppies (after their first vaccinations). We adapt the session to keep them stress-free.",
  },
  {
    q: "How often should I groom my pet?",
    a: "Most dogs benefit from a full grooming session every 4–6 weeks. Long-coated breeds may need it every 3–4 weeks, while short-coated breeds can stretch to 8 weeks. We can recommend the right schedule for your pet.",
  },
  {
    q: "Do you groom cats?",
    a: "Yes — we groom both dogs and cats. Cat grooming is gentle and unhurried, ideal for matting removal, de-shedding and sanitary trims.",
  },
];

export const TRUST_POINTS = [
  "Expert Certified Groomers",
  "Pet-Safe Products Only",
  "Stress-Free Environment",
  "Home Service Available",
  "Affordable Pricing",
  "5-Star Rated in Bhubaneswar",
];
